import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Typography, Button, Tooltip, Dialog, DialogContent, Chip,
    TextField, InputAdornment, IconButton, Menu, MenuItem,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { TablePagination } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import StarIcon from '@material-ui/icons/Star';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined';
import ExistingProgramDialog from '../DQProV2/components/ExistingProgramDialog.comp';
import { getInsiderProgramsService, publishLearningProgramService, linkLearningProgramService, getTemplateLearningProgramsService, duplicateLearningProgramService, getActiveChatInsiderProgramService, clearChatStateInsiderService, CurationPromptV2Service, getInsiderProgramService, buildLearningProgramService, generateLearningProgramContentService, getLearningProgramPreviewStatusService, getLearningProgramStatusService, updateLearningProgramService } from "../../../services/learning_programs.service";
import { getOrgModulesService, updateProgramRequestStatusService } from "../../../services/client.service";
import { refreshToken, getDQInsiderInfoService } from "../../../services/auth.service";
import { contactMailtoHref } from "../../../utils/contactEmail";
import { useAuth } from "../../../context/auth";
import { USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY, TOAST_TYPES, LEARNING_PROGRAM_STATUS, WORKSPACE_REQUEST_STATUS, WORKSPACE_ROLE, REQUEST_TYPE } from "../../../utils/constants";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { toast, toastWarning } from "../../../utils/utils";
import ProgramPublishBadge from "../components/ProgramPublishBadge.comp";
import { canCreateProduct, solutionPlanKey } from "../../../utils/productAccess";
import { PRODUCT_KEY, LEARNCREATOR_START_PATH } from "../../../utils/constants";
import AvailableLearningPrograms from "./components/shared/AvailableLearningPrograms.comp";
import { linkedOnlyProgramRows, mergeAvailablePage } from "./components/shared/linkedProgramRows";
import {
    ProgramPreviewDialog,
    ProgramSuggestionReviewDialog,
    ProgramRequestSentDialog,
    RequestProgramChangesDialog,
    groupKeyFor,
} from "./components/shared/ProgramSuggestionDialogs.comp";
import RequestLearningProgramsWizard from "./components/shared/RequestLearningProgramsWizard.comp";
import { listProgramRequestsService, createProgramRequestService, respondToSuggestionService, listOrgClassesService, listOrgSchoolsService } from "../../../services/client.service";

import PublishProgramDialogs from "../components/PublishProgramDialogs.comp";
import DuplicateProgramDialogs from "../components/DuplicateProgramDialogs.comp";
import AssignProgramToSchoolsDrawer from "../components/AssignProgramToSchoolsDrawer.comp";
import {getProgramPreviewPath} from "./DQProProgPreviewRedirect.page";
import {hasFailedContentSession} from "./DQProProgDashboard.page";

/*
 * Where a delivered program opens — getProgramPreviewPath, straight to its
 * preview (the program dashboard step is skipped). Same route the school request
 * page uses, so View lands in one place whichever screen it was pressed from.
 */

const isPublished = (status) => Number(status) === LEARNING_PROGRAM_STATUS.PUBLISHED;

const PUBLISH_TOOLTIP = 'Publish this learning program to make it accessible to assigned schools. Once published, the program can no longer be edited.';
const publishButtonTooltip = (p) => (!p.is_ready ? 'Complete all sessions before publishing.' : PUBLISH_TOOLTIP);

const DUPLICATE_TOOLTIP = 'Create an editable draft copy of this program. Links, learner responses and published status are not copied.';
const duplicateButtonTooltip = (p) => (!p.is_ready ? 'Wait for content generation to finish before duplicating.' : DUPLICATE_TOOLTIP);

const formatVisibilityScope = (value) => {
    if (value == null || value === '') return '—';
    const s = String(value).trim();
    return s || '—';
};

/**
 * Per-row "..." overflow menu for the My Program Library cards. Generic —
 * the caller builds the exact item list (label/onClick/disabled/tooltip/
 * danger) since which actions apply, and whether each is real or a "coming
 * soon" placeholder, varies per row (draft vs published, org-scoped vs not,
 * ready vs not). See the .map() below for what each row actually passes.
 */
const ProgramRowKebabMenu = ({ items }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const pick = (item) => {
        setAnchorEl(null);
        if (!item.disabled) item.onClick();
    };
    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreHorizOutlinedIcon style={{ fontSize: 20, color: 'var(--brand-text-muted, #9CA3AF)' }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {items.map((item) => (
                    <Tooltip key={item.label} title={item.tooltip || ''} placement="left" arrow>
                        <span>
                            <MenuItem
                                onClick={() => pick(item)}
                                disabled={!!item.disabled}
                                sx={{ fontSize: '13.5px', color: item.danger ? '#D32F2F' : undefined }}
                            >
                                {item.label}
                            </MenuItem>
                        </span>
                    </Tooltip>
                ))}
            </Menu>
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    tableContent: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
    },
    headerCell: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
    },
    cellText: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    cellTextLeft: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'left',
        wordBreak: 'break-word',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    viewBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 20px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        boxShadow: 'none !important',
        '&:hover': { background: '#c9541e !important' },
    },
    actionBtn: {
        borderRadius: '50px !important',
        padding: '7px 20px !important',
        // Stops the button from resizing/jumping width when the label switches
        // between "Publish" and "Publishing...".
        minWidth: '115px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
    },
    readMoreLink: {
        color: '#6A8EC9',
        fontSize: '12px',
        marginLeft: '4px',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 500,
        '&:hover': { textDecoration: 'underline' },
    },
    sectionTitle: {
        fontSize: '20px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        marginBottom: '8px !important',
        marginTop: '8px !important',
    },
    inProgressHeader: {
        display: 'grid',
        gridTemplateColumns: '2.4fr 2fr 1fr 1fr 1.1fr',
        padding: '0 20px',
        marginBottom: '12px',
        minWidth: '720px',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    inProgressRow: {
        display: 'grid',
        gridTemplateColumns: '2.4fr 2fr 1fr 1fr 1.1fr',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '14px 20px',
        alignItems: 'center',
        marginBottom: '10px',
        minWidth: '720px',
        border: '1px solid rgba(5, 47, 68, 0.15)',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            padding: '12px 16px',
            gap: 0,
        },
    },
    desktopOnly: { [theme.breakpoints.down('sm')]: { display: 'none !important' } },
    mobilePrimary: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
        },
    },
    mobilePrimaryText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--brand-text, #222)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileSubText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '12px',
            color: 'var(--brand-text-muted, #666)',
            marginTop: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileActions: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            gap: '6px',
            flexShrink: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
        },
    },
    mobileMetaRow: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, #f0f0f0)',
        },
    },
    mobileMetaTag: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--brand-text-muted, #555)',
            background: 'var(--brand-border, #f5f5f5)',
            borderRadius: '20px',
            padding: '2px 8px',
        },
    },

    // ── Page header + search/filter bar ──
    pageHeaderRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        flexWrap: 'wrap',
        marginBottom: '4px',
    },
    pageTitle: {
        fontSize: '30px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #111)',
        marginBottom: '4px !important',
    },
    pageSubtitle: {
        fontSize: '14px !important',
        color: 'var(--brand-text-muted, #6B7280)',
    },
    newProgramBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '9px 22px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': { background: '#c9541e !important' },
    },
    filterBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        margin: '24px 0 32px',
    },
    searchField: {
        minWidth: '260px',
        flex: '1 1 260px',
        maxWidth: '420px',
        '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            background: 'var(--brand-surface, #fff)',
        },
    },
    filterChip: {
        borderRadius: '50px !important',
        padding: '4px 16px !important',
        fontSize: '13px !important',
        fontWeight: '600 !important',
        textTransform: 'none !important',
        border: '1px solid var(--brand-border, #E5E7EB) !important',
        color: 'var(--brand-text-muted, #6B7280) !important',
        background: 'var(--brand-surface, #fff) !important',
        boxShadow: 'none !important',
        minWidth: 'unset !important',
        whiteSpace: 'nowrap',
    },
    filterChipActive: {
        borderColor: '#FF6900 !important',
        color: '#FF6900 !important',
        background: 'rgba(255,105,0,0.08) !important',
    },

    // ── "Start from a template" section ──
    useTemplateBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 18px !important',
        textTransform: 'none !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': { background: '#c9541e !important' },
    },

    // ── "My program library" cards ──
    libraryCard: {
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '14px',
        padding: '12px 18px',
        marginBottom: '8px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    libraryCardTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    libraryTitleGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        minWidth: 0,
    },
    libraryTitle: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #222)',
        wordBreak: 'break-word',
    },
    libraryCode: {
        fontSize: '11px !important',
        color: 'var(--brand-text-muted, #9CA3AF)',
    },
    libraryDescription: {
        fontSize: '13px !important',
        color: 'var(--brand-text-muted, #555)',
        wordBreak: 'break-word',
    },
    libraryMetaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
    },
    metaCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
        minWidth: '70px',
    },
    metaLabel: {
        fontSize: '10.5px !important',
        color: 'var(--brand-text-muted, #9CA3AF)',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
    },
    metaValue: {
        fontSize: '13px !important',
        fontWeight: '600 !important',
        color: 'var(--brand-text, #333)',
        whiteSpace: 'nowrap',
    },
    libraryActionsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        flexWrap: 'wrap',
        flexShrink: 0,
    },
    warningBanner: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '13px',
        color: '#9E7B00',
        background: 'rgba(158, 123, 0, 0.08)',
        border: '1px solid rgba(158, 123, 0, 0.25)',
        borderRadius: '10px',
        padding: '8px 14px',
    },
}));

/*
 * learning_programs carries two kinds of row: programs that were built, and
 * conversations that have not become one yet. is_completed tells them apart,
 * and this page renders both — the unfinished ones under "Continue where you
 * left off", the rest in the library table.
 *
 * `!== false` rather than `=== true` throughout: a response from an older API
 * carries no such field, and those rows are programs.
 */
/**
 * Titles come from the curation engine, which writes them however the
 * conversation phrased them — "Digital Citizenship" next to "digital
 * citizenship for Upper Primary". The list shows them side by side, so they
 * are cased here rather than left to however each conversation ran.
 *
 * Short joining words stay lower unless they open the title. Tokens that
 * already carry their own capitals are left untouched: re-casing "AI-Digital"
 * would produce "Ai-digital".
 */
const MINOR_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'vs', 'with']);

const capitalise = (w) => w.charAt(0).toUpperCase() + w.slice(1);

const titleCase = (t) => {
    const raw = String(t || '').trim();
    if (!raw) return raw;

    return raw.split(/\s+/).map((word, i) => {
        // Already has a capital past the first character — an acronym or a
        // name the engine set deliberately. Leave it exactly as it is.
        if (/[A-Z]/.test(word.slice(1))) return word;

        const lower = word.toLowerCase();
        if (i > 0 && MINOR_WORDS.has(lower)) return lower;

        // Each part of a hyphenated word takes a capital: "self-regulated"
        // becomes "Self-Regulated", which is how the competencies are written.
        return lower.split('-').map(capitalise).join('-');
    }).join(' ');
};

// Same test the program dashboard applies to the ML server's reply.
const mlRespFailed = (r) => !!r && (r.error === true
    || (r.http_code && r.http_code !== 200)
    || (r.status_code && r.status_code !== 200));

const REGENERATE_TOOLTIP = 'Try generating this program again. You will not be charged additional credit.';

const pickUnfinished = (learning_programs) =>
    (Array.isArray(learning_programs) ? learning_programs : []).filter((p) => p && p.is_completed === false);

const pickBuilt = (learning_programs) =>
    (Array.isArray(learning_programs) ? learning_programs : []).filter((p) => p && p.is_completed !== false);

const DQProLearnerProgramList = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation(); // used to trigger refetch on every navigation to this page
    const { authUser, setAuthUser, setAuthToken } = useAuth();
    const [learnerPrograms, setLearnerPrograms] = useState([]);

    /*
     * Resuming is the builder's default, so the only thing this page has to
     * decide is whether there is anything to resume. The id is kept because
     * "Start new" needs it to clear that session.
     */
    /*
     * In-progress conversations. Separate from `insiderPrograms` because they
     * are not programs yet — no module, no outline, nothing to publish — and
     * mixing them in would put un-openable rows in the built list.
     */
    const [drafts, setDrafts] = useState([]);
    const [deletingDraftId, setDeletingDraftId] = useState(null);
    /*
     * Deleting a draft removes the conversation, the module, the outline and the
     * program together, and there is no undo — so it is asked for, not assumed
     * from one click on a small icon.
     */
    const [draftPendingDelete, setDraftPendingDelete] = useState(null);

    const handleDeleteDraft = useCallback(async (programId) => {
        if (!programId || deletingDraftId) return;
        setDeletingDraftId(programId);
        try {
            await clearChatStateInsiderService(programId);
            // Drop it locally rather than refetch the whole list: the row is
            // gone either way, and this keeps the page from flickering.
            setDrafts((prev) => prev.filter((d) => d.program_id !== programId));
        } catch (e) {
            // invokeApi has already said why. The row stays, because it is
            // still there — removing it would claim a delete that did not
            // happen.
        } finally {
            setDeletingDraftId(null);
        }
    }, [deletingDraftId]);

    const [resumePromptOpen, setResumePromptOpen] = useState(false);
    const [activeChatProgramId, setActiveChatProgramId] = useState(null);
    const [startingNew, setStartingNew] = useState(false);

    /*
     * startNew tells the builder not to hunt for an active chat to restore.
     * Deleting the old program is not enough by itself — "active chat" is
     * workspace-wide, so with several drafts open the next one is simply
     * promoted and the person lands in a conversation they did not choose.
     */
    const goToBuilder = useCallback(() => {
        history.push('/dq_pro/program/creation/preview');
    }, [history]);
    /*
     * A NEW program starts on the LearnCreator landing page — the same page
     * every other entry point opens (bug sheet Ian-LC-30Sep-1). Typing a goal
     * there starts a fresh program in the builder (startFromLanding), so the
     * builder never restores some other chat. Only "Continue" goes straight
     * to the builder, to resume the unfinished program.
     */
    const goToLanding = useCallback(() => {
        history.push(LEARNCREATOR_START_PATH);
    }, [history]);

    const handleNewProgramClick = useCallback(async () => {
        let programId = null;
        try {
            const res = await getActiveChatInsiderProgramService();
            programId = res?.data?.program_id || null;
        } catch (e) {
            // Cannot tell — send them through rather than block the button.
            goToLanding();
            return;
        }
        if (!programId) {
            goToLanding();
            return;
        }
        setActiveChatProgramId(programId);
        setResumePromptOpen(true);
    }, [goToLanding]);

    const handleStartNewProgram = useCallback(async () => {
        if (startingNew) return;
        setStartingNew(true);
        try {
            if (activeChatProgramId) await clearChatStateInsiderService(activeChatProgramId);
        } catch (e) {
            // Clearing is best-effort; the builder still opens.
        } finally {
            setStartingNew(false);
            setResumePromptOpen(false);
            goToLanding();
        }
    }, [activeChatProgramId, startingNew, goToLanding]);

    const [insiderPrograms, setInsiderPrograms] = useState([]);
    // DQ-authored showcase templates — global, unpaginated, preview-only.
    // See src/docs/Template-Learning-Programs-Architecture.md (my-dq-api) §11.
    const [templatePrograms, setTemplatePrograms] = useState([]);
    // P1A — Available Learning Programs (slide 46) and the request wizard (13-15).
    const [suggestionRow, setSuggestionRow] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    // The accept / adjust / decline dialog for a partner-suggested row.
    const [suggestionReviewOpen, setSuggestionReviewOpen] = useState(false);
    // Slide 49 — the confirmation after Accept or Request changes.
    const [suggestionSentOpen, setSuggestionSentOpen] = useState(false);
    const [respondingSuggestion, setRespondingSuggestion] = useState(false);
    const [requestWizardOpen, setRequestWizardOpen] = useState(false);
    const [availableRows, setAvailableRows] = useState([]);
    const [availableLoading, setAvailableLoading] = useState(false);
    // Server-side paging, same contract as the partner request inbox.
    const [availablePage, setAvailablePage] = useState(0);
    const [availableRowsPerPage, setAvailableRowsPerPage] = useState(10);
    const [availableTotal, setAvailableTotal] = useState(0);
    // Slide status of every row across all pages — what the counters count.
    const [availableStatuses, setAvailableStatuses] = useState(null);
    // Status filter (LA-SNW, SW-03Nov-01): '' lists every status.
    const [availableStatusFilter, setAvailableStatusFilter] = useState('');
    // Review opens read-only for anything but a suggestion still awaiting the school.
    const [reviewReadOnly, setReviewReadOnly] = useState(false);
    // Program Ready → Request Changes popup.
    const [changesOpen, setChangesOpen] = useState(false);
    // Real schools and classes for the request wizard's group picker (slide 14).
    const [wizardSchools, setWizardSchools] = useState([]);
    const [wizardClasses, setWizardClasses] = useState([]);
    // Grades read off the class records — see the loader below.
    const [wizardGrades, setWizardGrades] = useState([]);

    /*
     * SW-47 "Class / group" options — the school's own grades and classes, the
     * same lists the request wizard offers. In a network a class name repeats
     * across schools, so a suggestion addressed to one school only offers that
     * school's classes.
     */
    const suggestionGroupOptions = (() => {
        const inst = suggestionRow?.raw?.institute_code || '';
        const grades = (wizardGrades || []).map((g) => ({
            key: groupKeyFor(g.value, ''), label: g.label, grade_level: g.value, class_name: '',
        }));
        const classes = (wizardClasses || [])
            .filter((c) => !inst || !c.institute_code || c.institute_code === inst)
            .map((c) => ({
                key: groupKeyFor('', c.value), label: c.value, grade_level: '', class_name: c.value,
            }));
        const seen = new Set();
        return [...grades, ...classes].filter((o) => (seen.has(o.key) ? false : seen.add(o.key)));
    })();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
    const [tokenRefreshed, setTokenRefreshed] = useState(false);
    // null = idle; string = module code currently being published (only that button shows loading)
    const [publishingCode, setPublishingCode] = useState(null);
    // null = idle; string = module code currently being duplicated (same one-at-a-time
    // pattern as publishingCode, so only the clicked row shows a loading label).
    const [duplicatingCode, setDuplicatingCode] = useState(null);
    // null = dialogs closed. { program, stage: 'confirm' | 'success', name, created }
    // drives the two-step Duplicate flow: name the copy, then offer to preview it.
    const [duplicateDialog, setDuplicateDialog] = useState(null);
    // null = dialogs closed; { program, stage: 'confirm' | 'success' } drives the Publish flow.
    const [publishDialog, setPublishDialog] = useState(null);
    // School this publish delivered to, learned from the delivery response — drives
    // the confirm-before-link step (Lydia, slide 40 thread). null = ordinary publish.
    const [deliveredTarget, setDeliveredTarget] = useState(null);
    const [linkConfirmOpen, setLinkConfirmOpen] = useState(false);
    const [linkingToSchool, setLinkingToSchool] = useState(false);
    // The published program the "Assign to schools" drawer is open for.
    const [assignProgram, setAssignProgram] = useState(null);
    // Search/status filter for My Program Library — client-side only, scoped
    // to the currently loaded page. Neither getOrgModulesService nor
    // getInsiderProgramsService accepts a search param today, so this can't
    // reach across pages until the backend adds one.
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'published' | 'draft'

    // Level helpers — use loose equality so string "21"/"22" from the API
    // is treated the same as the numeric constant.
    // eslint-disable-next-line eqeqeq
    const isLevel21 = authUser?.access_level == USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    // eslint-disable-next-line eqeqeq
    const isLevel22 = authUser?.access_level == USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const isPaidUser = authUser?.dq_pro_plan?.status === 'active' && authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;
    // Level 21 on default plan sees insider programs (same as non-org users)
    const isOrgScopedUser = isLevel22 || (isLevel21 && isPaidUser);

    // Where "Assign to ..." sends a published program: always the next level
    // down. Partners and networks assign to schools; a school — a lead at 22,
    // or a direct-plan school's owner at 21 — assigns to its classes. Teachers
    // link their own programs from Manage Classes, so they get no button.
    // eslint-disable-next-line eqeqeq
    const isTeacherUser = authUser?.access_level == USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL;
    const isSchoolAccount = isLevel22
        || (isLevel21 && authUser?.is_direct_workspace === true && authUser?.is_school_network !== true);
    const assignTarget = isTeacherUser ? null : (isSchoolAccount ? 'classes' : 'schools');

    // Workspace owner — the account holder, as opposed to every other role that
    // can hold a valid insider session. Peers inherit their creator's
    // access_level (so they appear at 20/21/22 alike) and `is_peer` is the only
    // unambiguous discriminator; individual learners are 25.
    //
    // `workspace_role` is the third test, and the level cannot stand in for it:
    // a school NETWORK's contact is provisioned at 21 — the same level as a
    // partner on a single-institute plan — because the network sits one level
    // above its schools. It is nonetheless a CLIENT of the partner's workspace
    // and shares its dq_insider_id, so level alone read every network as the
    // account holder and fired the owners-only Sample Programs request on their
    // behalf, which the API answered 403 WORKSPACE_OWNER_REQUIRED — surfacing as
    // an error toast on a page that had otherwise loaded correctly.
    //
    // Mirrors isWorkspaceOwner in my-dq-api's workspace_owner_guard.mid.js,
    // including its fallback: with no role recorded the level decides, so a
    // legacy account keeps exactly the rights it has today.
    // eslint-disable-next-line eqeqeq
    const isLevel20 = authUser?.access_level == USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL;
    const isWorkspaceClient = authUser?.workspace_role
        ? authUser.workspace_role === WORKSPACE_ROLE.CLIENT
        : isLevel22;
    const isWorkspaceOwner = (isLevel20 || isLevel21)
        && authUser?.is_peer !== true
        && !isWorkspaceClient;

    // Creator-or-owner: a teacher's program may only be published/managed by
    // that teacher or the workspace owner — not by a school viewing it, nor
    // by a teacher viewing a partner's program. created_by_user_id is null on
    // programs that predate attribution; those fall back to owner-only.
    // See docs/features/Teacher-Role-and-School-Workspace.md §8.
    const canManageProgram = (p) => p.created_by_user_id === authUser?._id || isWorkspaceOwner;

    // Duplicating is deliberately NOT creator-or-owner. Anyone who can author a
    // program may copy any program they can see — a teacher taking the partner's
    // program to get an editable version of their own is the primary use case,
    // and canManageProgram would block exactly that.
    //
    // The real gate is the CREATE product permission, which is also what the API
    // enforces (productGuard + capabilityGuard on /learning_program/duplicate).
    // This only keeps the UI from offering what the API would refuse.
    //
    // Only finished programs can be copied: the API rejects an unfinished source
    // with reason `program_not_ready`, because a half-generated program would
    // copy mid-write.
    const canCreatePrograms = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const canDuplicateProgram = (p) => canCreatePrograms && !!p.is_ready;

    // On mount: for org-scoped users always refresh the token first so the JWT
    // is guaranteed to carry the latest org_code before any fetch is made.
    // For non-org users skip the refresh and go straight to fetching.
    // useEffect(() => {
    //     if (!isOrgScopedUser) {
    //         setTokenRefreshed(true);
    //         return;
    //     }
    //     refreshToken()
    //         .then(response => {
    //             const { auth_token, user } = response?.data || {};
    //             if (auth_token) setAuthToken(auth_token);
    //             if (user) setAuthUser(user);
    //         })
    //         .catch(() => { /* token refresh failed — proceed with existing token */ })
    //         .finally(() => setTokenRefreshed(true));
    // }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchPrograms(page + 1, rowsPerPage);
    }, [page, rowsPerPage, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sample Programs — a small, global, admin-curated catalogue, for workspace
    // owners only. Everyone else who can reach this page is a downstream role,
    // not the account holder: clients (22), peers/colleagues (is_peer, at any
    // level), and individual learner accounts (25). Fetched once, independent
    // of the page/rowsPerPage cycle above.
    useEffect(() => {
        if (!isWorkspaceOwner) return;
        getTemplateLearningProgramsService()
            .then((res) => setTemplatePrograms(res?.data?.templates || []))
            .catch(() => setTemplatePrograms([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Normalise an org-module record to the shape expected by the table.
     *  Handles both `module_code`/`module_name` and bare `code`/`name` field variants.
     *  Org modules predate the status field for programs already linked, so
     *  default to Published (1) when the backend hasn't attached it yet. */
    const normaliseModule = (m) => ({
        code: m.module_code || m.code,
        full_name: m.module_name || m.full_name || m.name,
        description: m.module_description || m.description || '',
        createdAt: m.createdAt || null,
        time_to_complete_minutes: m.time_to_complete_minutes || m.duration || '',
        visibility_scope: m.visibility_scope ?? null,
        is_ready: m.is_ready ?? true,
        status: m.status ?? LEARNING_PROGRAM_STATUS.PUBLISHED,
    });

    /** Parse an org-modules API response into a { modules, pagination } object. */
    const parseOrgResponse = (response, apiPage, limit) => {
        const payload = response?.data ?? response ?? {};
        const rawModules = payload?.modules || payload?.learning_programs || [];
        const pg = payload?.pagination || { total: rawModules.length, page: apiPage, limit };
        return { modules: rawModules.map(normaliseModule), pagination: pg };
    };

    const fetchPrograms = (apiPage, limit) => {
        setLoading(true);

        if (isLevel21 && isPaidUser) {
            // Level 21 paid: fetch both in parallel; we need both results before we can
            // determine which insider programs are still in-progress (not yet in org modules).
            Promise.allSettled([
                getOrgModulesService({ org_code: authUser?.org_code, module_type: 'learning_program', page: apiPage, limit }),
                getInsiderProgramsService(1, 100),
            ]).then(([orgResult, insiderResult]) => {
                // ── Org modules (completed programs → main table) ──
                const orgResponse = orgResult.status === 'fulfilled' ? orgResult.value : null;
                const { modules, pagination: pg } = parseOrgResponse(orgResponse, apiPage, limit);
                setLearnerPrograms(modules);
                setPagination(pg);

                // ── In-progress insider programs ──
                // Exclude any insider program whose code already appears in org modules
                // (those have been completed and converted on the backend).
                if (insiderResult.status === 'fulfilled' && insiderResult.value) {
                    const insiderPayload = insiderResult.value?.data ?? insiderResult.value ?? {};
                    const insiderRows = insiderPayload?.learning_programs || insiderPayload?.modules || [];
                    // Only built programs can be compared against org modules —
                    // a conversation has no code to match on.
                    const allInsider = pickBuilt(insiderRows).map(p => ({
                        ...p,
                        visibility_scope: p.visibility_scope ?? null,
                    }));
                    const orgCodeSet = new Set(modules.map(m => m.code).filter(Boolean));
                    // A program is still in-progress if its code is absent from org modules,
                    // or if it has no code yet (still being built).
                    const inProgress = allInsider.filter(p => !p.code || !orgCodeSet.has(p.code));
                    setInsiderPrograms(inProgress);
                    // Conversations that have not been built yet. They come from
                    // the chat collection, so they are never in org modules and
                    // need no de-duplicating against them.
                    setDrafts(pickUnfinished(insiderRows));
                } else {
                    setInsiderPrograms([]);
                    setDrafts([]);
                }

                setLoading(false);
            }).catch(() => {
                setLearnerPrograms([]);
                setInsiderPrograms([]);
                setLoading(false);
            });

        } else if (isLevel22) {
            // Level 22: org modules only (no in-progress section).
            getOrgModulesService({ org_code: authUser?.org_code, module_type: 'learning_program', page: apiPage, limit })
                .then(response => {
                    const { modules, pagination: pg } = parseOrgResponse(response, apiPage, limit);
                    setLearnerPrograms(modules);
                    setPagination(pg);
                    setLoading(false);
                })
                .catch(() => {
                    setLearnerPrograms([]);
                    setLoading(false);
                });

        } else {
            // Level 20 (and any other): insider programs only.
            getInsiderProgramsService(apiPage, limit)
                .then(response => {
                    const rows = response?.data?.learning_programs || [];
                    const programs = pickBuilt(rows).map((p) => ({
                        ...p,
                        visibility_scope: p.visibility_scope ?? null,
                    }));
                    const paginationData = response?.data?.pagination || { total: programs.length, page: apiPage, limit };
                    setLearnerPrograms(programs);
                    setPagination(paginationData);
                    // The unbuilt half of the same response.
                    setDrafts(pickUnfinished(rows));
                    setLoading(false);
                })
                .catch(() => {
                    setLearnerPrograms([]);
                    setDrafts([]);
                    setLoading(false);
                });
        }
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
    const handleViewProgram = (p) => history.push(getProgramPreviewPath(p.code), { program: p });

    /*
     * Generation failures. A built program that is neither ready nor published is
     * either still generating or has failed, and the list cannot tell which, so
     * each one is asked for its own status (the same two calls the program
     * dashboard polls) and Regenerate is offered only on a real failure.
     * code -> 'outline' | 'content', the stage that failed.
     */
    const [failedPrograms, setFailedPrograms] = useState({});
    const [regeneratingId, setRegeneratingId] = useState(null);
    // Programs restarted from here. Their status can still read "failed" for a
    // moment after the retry is accepted, which would bring the button back.
    const regeneratedRef = useRef(new Set());

    useEffect(() => {
        if (isLevel22) return undefined;
        const candidates = [...learnerPrograms, ...insiderPrograms].filter((p) => p?.code
            && !isPublished(p.status) && p.is_ready === false
            && canManageProgram(p) && !regeneratedRef.current.has(p.code));
        if (candidates.length === 0) {
            setFailedPrograms({});
            return undefined;
        }
        let cancelled = false;
        const failedStage = async (code) => {
            const outline = (await getLearningProgramPreviewStatusService(code))?.data || {};
            if (outline.progress_status === -1 || mlRespFailed(outline.ml_server_resp)) return 'outline';
            if (!outline.generation_initiated) return null;
            const content = (await getLearningProgramStatusService(code))?.data || {};
            if (hasFailedContentSession(content.contentStatus) || mlRespFailed(content.ml_server_resp)) return 'content';
            return null;
        };
        Promise.all(candidates.map((p) => failedStage(p.code).then((stage) => [p.code, stage]).catch(() => [p.code, null])))
            .then((entries) => {
                if (cancelled) return;
                setFailedPrograms(Object.fromEntries(entries.filter(([, stage]) => stage)));
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [learnerPrograms, insiderPrograms, isLevel22]);

    /** Regenerate a built program from the stage that failed. */
    const handleRegenerateProgram = async (p) => {
        const stage = failedPrograms[p?.code];
        if (!stage || regeneratingId) return;
        setRegeneratingId(p.code);
        try {
            if (stage === 'outline') {
                const d = (await getInsiderProgramService(p.code))?.data?.learning_program;
                if (!d) throw new Error('Program not found');
                await buildLearningProgramService(
                    p.code, d.full_name, d.target_audience, d.dq_codes, d.time_to_complete_minutes,
                    d.image_url, d.theme_color, d.badge_background, d.primary_color, d.description,
                    d.more_info, d.translations, d.program_details, REQUEST_TYPE.RETRY,
                );
            } else {
                await generateLearningProgramContentService(p.code, REQUEST_TYPE.RETRY);
            }
            regeneratedRef.current.add(p.code);
            setFailedPrograms((prev) => { const { [p.code]: _gone, ...rest } = prev; return rest; });
            toast(TOAST_TYPES.SUCCESS, 'Regenerating', 'We are generating this program again.');
        } catch (e) {
            toastWarning('Failed to regenerate this program. Please try again.');
        } finally {
            setRegeneratingId(null);
        }
    };

    /** Regenerate a draft whose build failed to start: the chat's own "retry build". */
    const handleRegenerateDraft = async (d) => {
        if (!d?.program_id || regeneratingId) return;
        setRegeneratingId(d.program_id);
        try {
            const card = (await CurationPromptV2Service(d.program_id, 'retry build'))?.data?.card?.type;
            if (card === 'build_started') {
                toast(TOAST_TYPES.SUCCESS, 'Regenerating', 'We are generating this program again.');
                fetchPrograms(page + 1, rowsPerPage);
            } else if (card === 'insufficient_credits') {
                toastWarning('There are not enough credits to generate this program yet.');
            } else {
                toastWarning('Failed to regenerate this program. Please try again.');
            }
        } catch (e) {
            // invokeApi has already said why.
        } finally {
            setRegeneratingId(null);
        }
    };

    /** Delete a built program that is not published: same removal as a draft. */
    const handleDeleteProgram = async (target) => {
        if (!target?.program_id || deletingDraftId) return;
        setDeletingDraftId(target.program_id);
        try {
            await clearChatStateInsiderService(target.program_id);
            toast(TOAST_TYPES.SUCCESS, 'Deleted', `${target.title || 'The program'} was deleted.`);
            fetchPrograms(page + 1, rowsPerPage);
        } catch (e) {
            // invokeApi has already said why. The row stays: it is still there.
        } finally {
            setDeletingDraftId(null);
        }
    };

    const askDeleteProgram = (p) => setDraftPendingDelete({ program_id: p.code, title: p.full_name, isProgram: true });

    /** Rename (LA-PW,SNW,SW,TW-19Oct-01) — the existing learning_program/update
     *  call with full_name; the API only lets the program's owner change it. */
    const [renameTarget, setRenameTarget] = useState(null); // { code, name }
    const [renaming, setRenaming] = useState(false);
    const handleRenameProgram = async () => {
        const name = String(renameTarget?.name || '').trim();
        if (!renameTarget?.code || !name) return;
        setRenaming(true);
        try {
            await updateLearningProgramService(renameTarget.code, { full_name: name });
            toast(TOAST_TYPES.SUCCESS, 'Renamed', `The program is now called ${name}.`);
            setRenameTarget(null);
            fetchPrograms(page + 1, rowsPerPage);
        } catch (e) {
            // invokeApi has already said why. The dialog stays open to try again.
        } finally {
            setRenaming(false);
        }
    };

    /** Regenerate / Delete for a row of the in-progress table. */
    const renderProgramRecoveryButtons = (p) => {
        if (isPublished(p.status) || !canManageProgram(p)) return null;
        return (
            <>
                {failedPrograms[p.code] && (
                    <Tooltip title={REGENERATE_TOOLTIP} arrow>
                        <span>
                            <Button className={classes.actionBtn} variant="outlined"
                                disabled={regeneratingId === p.code}
                                onClick={() => handleRegenerateProgram(p)}>
                                {regeneratingId === p.code ? 'Regenerating...' : 'Regenerate'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title="Permanently remove this draft." arrow>
                    <span>
                        <IconButton size="small" disabled={deletingDraftId === p.code}
                            onClick={() => askDeleteProgram(p)}
                            sx={{ color: 'var(--brand-text-muted, #9CA3AF)', '&:hover': { color: '#DC2626' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </>
        );
    };

    // Templates are preview-only — never the owner dashboard (no edit/publish/link
    // affordances apply to them).
    //
    // Routed to the V2 creation preview, NOT /dq_pro/products/program/:code/preview:
    // that route has no other caller left in the app, so it is the one at risk of
    // rotting or being removed. This page is the mainstream preview surface (it is
    // where the dashboard's own "Preview" action goes) and it renders the very same
    // DQProProgPreview component internally via ContentArea — so the slides look
    // identical, on a path the rest of the app keeps exercising.
    const handlePreviewTemplate = (p) => history.push('/dq_pro/program/creation/preview', {
        programCode: p.code,
        readOnly: true,
    });

    /** Publish a learning program (one-way — no unpublish). Enabled only when
     *  content generation is fully complete (`is_ready`) and it's still a Draft. */
    const handlePublish = async (program) => {
        if (!program?.code || publishingCode || isPublished(program.status) || !program.is_ready) return;
        setPublishingCode(program.code);
        try {
            const res = await publishLearningProgramService(program.code);
            if (res?.data) {
                toast(TOAST_TYPES.SUCCESS, 'Published', res.data.already_published
                    ? 'This program is already published.'
                    : 'Learning program published successfully.');

                // If this program was built for a School's learning-program request,
                // mark that request delivered (the API emails the school). Resolved
                // server-side from the program code, so it works no matter where the
                // build started. No-op for ordinary programs. Best effort — never
                // disturbs publishing.
                //
                // The response carries the request itself, which is how this page
                // learns WHICH school to offer linking to — no extra lookup, and it
                // works in a fresh session where no browser state survives.
                updateProgramRequestStatusService({
                    program_code: program.code,
                    status: WORKSPACE_REQUEST_STATUS.BUILT,
                }).then((reqRes) => {
                    const request = reqRes?.data?.request;
                    if (request?.org_code) {
                        setDeliveredTarget({
                            org_code: request.org_code,
                            school_name: request.school_name || request.org_code,
                            program_code: program.code,
                        });
                    }
                }).catch(() => {});

                fetchPrograms(page + 1, rowsPerPage);
                setPublishDialog((prev) => (prev && prev.program.code === program.code ? { ...prev, stage: 'success' } : prev));
            }
        } catch (err) {
            // invokeApi already surfaces the backend error message as a toast
            // (e.g. "Complete all sessions before publishing.").
            setPublishDialog(null);
        } finally {
            setPublishingCode(null);
        }
    };

    /**
     * Step 1 — open the naming dialog, prefilled with the API's own default so
     * confirming without typing gives exactly what the backend would have chosen.
     */
    const openDuplicateConfirm = (program) => {
        if (!program?.code || duplicatingCode || !canDuplicateProgram(program)) return;
        setDuplicateDialog({
            program,
            stage: 'confirm',
            name: `Copy of ${program.full_name || 'Untitled program'}`,
            created: null,
        });
    };

    /**
     * Step 2 — duplicate a program into a new Draft owned by the current user.
     *
     * Nothing is regenerated server-side, so this returns as fast as the content
     * (and its assets) can be copied — no polling, no job to wait on. The new
     * program appears in this same list as a Draft once refetched.
     *
     * On success the dialog moves to its 'success' stage rather than closing, so
     * the user can jump straight into the copy. The list is refetched either way,
     * so it is already correct behind the dialog if they decline.
     */
    const handleDuplicate = async () => {
        const program = duplicateDialog?.program;
        const full_name = String(duplicateDialog?.name || '').trim();
        if (!program?.code || duplicatingCode || !full_name) return;

        setDuplicatingCode(program.code);
        try {
            const res = await duplicateLearningProgramService(
                program.code,
                full_name,
                // Lets the API collapse a genuine network retry of THIS click into
                // one program. The disabled button is what stops a second
                // deliberate click.
                `${program.code}-${Date.now()}`,
            );
            if (res?.data) {
                fetchPrograms(page + 1, rowsPerPage);
                setDuplicateDialog((prev) => (
                    prev && prev.program.code === program.code
                        ? { ...prev, stage: 'success', created: res.data }
                        : prev
                ));
            }
        } catch (err) {
            // invokeApi already surfaces the backend message, which is specific
            // per failure reason (not ready / limit reached / insufficient credits).
            setDuplicateDialog(null);
        } finally {
            setDuplicatingCode(null);
        }
    };

    /** "Use Template" is a frontend-only placeholder for now — Preview already
     *  works (handlePreviewTemplate), but actually copying a template into an
     *  editable program isn't wired to a service call yet. */
    const handleUseTemplate = () => {
        toast(TOAST_TYPES.INFO, 'Use Template', 'This feature is coming soon.');
    };

    /** "Assign to schools" is a frontend-only placeholder — there's no
     *  per-school assignment flow on this page yet ("Link Org-wide" is a
     *  different, coarser action: org-wide visibility, not assigning to
     *  specific schools). A published program can still be opened via the
     *  kebab menu's "View Program" item. */
    /** Open the assignment drawer (Network deck slide 62, Partner deck slide 63). */
    const handleAssignToSchools = (program) => {
        if (program?.code) setAssignProgram(program);
    };

    /**
     * Open the freshly duplicated program in the builder's preview.
     *
     * Deliberately WITHOUT `readOnly` — that flag routes the preview through the
     * templates/* endpoints, which verify the code is a published template. A
     * duplicate is an ordinary program the caller owns, so it must go through the
     * generic preview endpoints. (Contrast handlePreviewTemplate above.)
     */
    const handlePreviewDuplicate = () => {
        const code = duplicateDialog?.created?.code;
        setDuplicateDialog(null);
        if (code) history.push('/dq_pro/program/creation/preview', { programCode: code });
    };

    // Opens the "are you sure?" confirmation dialog for a given program row —
    // the actual publish call only happens once the user confirms.
    const openPublishConfirm = (program) => {
        if (!program?.code || publishingCode || isPublished(program.status) || !program.is_ready) return;
        setPublishDialog({ program, stage: 'confirm' });
    };

    const handleGoToMaterials = () => {
        const program = publishDialog?.program;
        setPublishDialog(null);
        if (program) handleViewProgram(program);
    };

    /**
     * When the published program fulfilled a school's request we know exactly
     * which org to link to, so confirm the school and link right here. An
     * ordinary publish still falls back to the client list, unchanged.
     */
    const handleLinkToSchool = () => {
        setPublishDialog(null);
        if (deliveredTarget?.org_code) {
            setLinkConfirmOpen(true);
            return;
        }
        history.push('/dq_pro/clients/list');
    };

    /** Perform the link once the owner has confirmed the school. */
    const confirmLinkToSchool = async () => {
        if (!deliveredTarget?.org_code || linkingToSchool) return;
        setLinkingToSchool(true);
        try {
            const res = await linkLearningProgramService(
                deliveredTarget.org_code, [deliveredTarget.program_code],
            );
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Linked',
                    `${deliveredTarget.school_name} can now open this program from their workspace.`);
                setLinkConfirmOpen(false);
            }
        } catch {
            /* invokeApi surfaces the backend error (e.g. no license seats) */
        } finally {
            setLinkingToSchool(false);
        }
    };


    const toggleDescription = (code) =>
        setExpandedDescriptions(prev => ({ ...prev, [code]: !prev[code] }));

    // Full timestamp (down to seconds) for the Date Created column,
    // rendered in the viewer's local timezone.
    // "2026-07-08T13:45:22.000Z" -> "2026-07-08 21:45:22" (e.g. UTC+8)
    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        const date = (d instanceof Date) ? d : new Date(d);
        if (isNaN(date.getTime())) return 'N/A';
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
            + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const truncate = (text, limit = 15) => {
        if (!text) return '';
        const words = text.split(' ');
        return words.length <= limit ? text : words.slice(0, limit).join(' ') + '...';
    };

    /* Defined before the data hooks below, which key off it. See the fuller note
       on the branch itself for why canCreateProduct is the discriminator. */
    const isP1AAccount = solutionPlanKey(authUser?.dq_pro_plan?.plan_key) === DQ_PRO_PLAN_KEY.ENTERPRISE_P1
        && !canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);

    /*
     * "Contact Partner" on a declined row (Ian, 8 Sep): a mailto link to the
     * partner (utils/contactEmail). A school's workspace
     * profile IS its partner's — the insider info Account Settings reads — so
     * its email is the partner's. No extra endpoint.
     */
    const [partnerEmail, setPartnerEmail] = useState('');
    const isDirectWorkspace = !!authUser?.is_direct_workspace;
    useEffect(() => {
        if (!isP1AAccount || isDirectWorkspace) return undefined;
        let alive = true;
        getDQInsiderInfoService()
            .then((res) => { if (alive) setPartnerEmail(res?.data?.profile?.email || ''); })
            .catch(() => { /* no address — Contact Partner says so */ });
        return () => { alive = false; };
    }, [isP1AAccount, isDirectWorkspace]);

    /*
     * ── P1A · Available Learning Programs, live data ──────────────────────────
     *
     * Reads the SAME endpoint the partner's request inbox uses. The list
     * controller forces org_code from the token for a school caller, so a school
     * can only ever see rows raised for, or suggested to, itself.
     */

    /**
     * WorkspaceRequest → the six statuses slide 46 prints.
     *
     * A DELIVERED row splits three ways, and the list endpoint already returns
     * everything needed to tell them apart:
     *
     *   Program Ready  built and complete; also still shown after the partner
     *                  publishes, until the partner links it to the school
     *   Live           linked to the school, "not yet link to students" —
     *                  action Link Programs
     *   Linked         "linked to students" — action View Dashboard
     *
     * program_linked and program_in_classes come from _attachLinkedState on the
     * API, which reads the org_code + program_code pair in OrgModules. No extra call.
     */
    const toSlideStatus = (r) => {
        const status = Number(r.status);
        if (status === WORKSPACE_REQUEST_STATUS.DECLINED) return 'Declined';
        if (status === WORKSPACE_REQUEST_STATUS.BUILT) {
            /*
             * Published is not Live by itself (Ian, 5 Oct 2026): the school can
             * do nothing with a program until the partner links it.
             *   not linked to the school yet      → still Program Ready
             *   linked to the school               → Live   (Link Programs)
             *   and given to its classes/learners  → Linked (View Dashboard)
             * program_linked / program_in_classes come from the API.
             */
            if (!r.program_linked) return 'Program Ready';
            return r.program_in_classes ? 'Linked' : 'Live';
        }
        // Built and complete but not yet published: the school can View and
        // Publish it — Program Ready.
        if (status === WORKSPACE_REQUEST_STATUS.IN_PROGRESS && r.program_is_ready) return 'Program Ready';
        // Pending, changes-requested, accepted and still-building await delivery.
        return String(r.source) === 'suggested' ? 'Suggested by Partner' : 'Requested by School';
    };

    const fmtReceived = (r) => {
        /*
         * A sent suggestion still waiting on the school was RECEIVED when the
         * partner sent it, not when the datahouse drafted it (often weeks
         * earlier) — the send is its last update while it waits.
         */
        const sentSuggestion = String(r.source) === 'suggested' && Number(r.status) === WORKSPACE_REQUEST_STATUS.PENDING;
        // suggested_at is that send, kept after the school answers — declining used
        // to switch the date back to the draft's (Oct 16 → Oct 15). Rows sent before
        // it was recorded keep the old rule.
        const when = (String(r.source) === 'suggested' && r.suggested_at)
            || (sentSuggestion ? (r.updatedAt || r.createdAt) : r.createdAt);
        const d = when ? new Date(when) : null;
        if (!d || Number.isNaN(d.getTime())) return '—';
        // Fixed to 'en-US': the browser's locale showed "9월 24일" on a Korean browser.
        return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    };

    /* "· NEW" marks a row the school has not opened yet — the same signal the
       delivery badge counts, so the two never disagree. */
    const isNew = (r) => !r.school_seen_at
        && Number(r.status) === WORKSPACE_REQUEST_STATUS.PENDING;

    /**
     * Send the school's answer, then refresh the list.
     *
     * The dialog returns its dropdown values as strings ("5 sessions",
     * "45 minutes"), so the digits are read out of them. A string the regex
     * cannot read is sent as undefined, which the API treats as "unchanged"
     * rather than guessing a number.
     */
    const respondToSuggestion = useCallback(async (status, picked = {}) => {
        const request_id = suggestionRow?.raw?._id;
        if (!request_id || respondingSuggestion) return;

        const digits = (v) => {
            const m = String(v ?? '').match(/\d+/);
            return m ? Number(m[0]) : undefined;
        };

        setRespondingSuggestion(true);
        try {
            const res = await respondToSuggestionService({
                request_id,
                status,
                num_sessions:          digits(picked.sessions),
                // SW-47 Class / group — '' on both is whole school.
                grade_level:           picked.grade_level,
                class_name:            picked.class_name,
                requested_changes:     picked.requested_changes,
                decline_reason:        picked.decline_reason,
            });
            if (res?.error) {
                toast(TOAST_TYPES.ERROR, 'Could not send your response', res.error_message || 'Please try again.');
                return;
            }
            setSuggestionReviewOpen(false);
            // Slide 49 follows Accept and Request changes; a decline has no screen.
            if (status !== WORKSPACE_REQUEST_STATUS.DECLINED) setSuggestionSentOpen(true);
            // The row changes status, so the list and its counters both move.
            loadAvailable();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not send your response', e?.message || 'Please try again.');
        } finally {
            setRespondingSuggestion(false);
        }
        // loadAvailable is declared below; it is stable and only called here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestionRow, respondingSuggestion]);

    const loadAvailable = useCallback(async () => {
        setAvailableLoading(true);
        try {
            /*
             * The slide statuses are worked out here, not by the API, and linked
             * programs with no request are added here too. So a status filter
             * reads every request (100 a page, the API's maximum) and pages the
             * matching rows itself; with no filter the server pages as before.
             */
            const filtering = !!availableStatusFilter;
            const res = await listProgramRequestsService({
                page:  filtering ? 1 : availablePage + 1,
                limit: filtering ? 100 : availableRowsPerPage,
            });
            /*
             * The partner's unsent drafts (status 10, its Ready to Suggest tab)
             * never arrive here: the API leaves them out for a school caller,
             * before paging, so a page of ten is ten rows and the total is real.
             */
            let docs = res?.data?.requests || res?.data?.docs || [];
            if (filtering) {
                const all = Number(res?.data?.total) || 0;
                for (let p = 2; docs.length < all; p += 1) {
                    const more = await listProgramRequestsService({ page: p, limit: 100 });
                    const next = more?.data?.requests || more?.data?.docs || [];
                    if (!next.length) break;
                    docs = docs.concat(next);
                }
            }
            const requestRows = docs.map((r) => ({
                id:       r._id,
                received: fmtReceived(r),
                isNew:    isNew(r),
                program:  r.topic || '—',
                cosArea:  (r.associated_dq_codes || []).join(', ') || r.topic || '—',
                group:    [r.grade_level, r.class_name].filter(Boolean).join(' · ') || 'Whole school',
                learners: r.learner_count ?? '—',
                /*
                 * A partner suggestion shows its session count only — session length is
                 * hidden in this flow (Ian, 17 Sep), even on a row that still stores one.
                 */
                duration: String(r.source) === 'suggested' && r.num_sessions
                    ? `${r.num_sessions} session${Number(r.num_sessions) === 1 ? '' : 's'}`
                    : r.num_sessions && r.session_duration_mins
                    ? `${r.num_sessions} × ${r.session_duration_mins} min`
                    // A request sent since session length became fixed (Ian-SW-01Oct-1)
                    // carries no minutes — its count, as the Pipeline shows it.
                    : r.num_sessions
                    ? `${r.num_sessions} session${Number(r.num_sessions) === 1 ? '' : 's'}`
                    : '—',
                status:   toSlideStatus(r),
                // A change request the partner has not dealt with yet: the school
                // waits, and cannot send another — the partner makes the change and publishes.
                pendingChange: [WORKSPACE_REQUEST_STATUS.ACCEPTED, WORKSPACE_REQUEST_STATUS.IN_PROGRESS].includes(Number(r.status))
                    && !!String(r.requested_changes || '').trim(),
                /*
                 * The partner is working on it (accepted, or being built): the
                 * school can still ask for changes before it is published.
                 * Program Ready carries Request Changes in its own legend.
                 */
                actions:  [WORKSPACE_REQUEST_STATUS.ACCEPTED, WORKSPACE_REQUEST_STATUS.IN_PROGRESS].includes(Number(r.status))
                    && toSlideStatus(r) !== 'Program Ready'
                    ? ['Review', 'Request Changes']
                    // Published: it can be viewed, and Link Programs is offered beside
                    // View; changes can no longer be requested.
                    : Number(r.status) === WORKSPACE_REQUEST_STATUS.BUILT && !r.program_linked
                    ? ['View', 'Link Programs']
                    : undefined,
                raw:      r,
            }));
            const states = res?.data?.scope_states;
            /*
             * Programs LINKED to this school or network with no request behind
             * them (the partner, or the network, linked them from My Schools).
             * They are the account's programs too, so they are listed after the
             * requests as Live or Linked and counted with them. A program that
             * has a request is already a row above and is not repeated.
             * Display only: if they cannot be read, the requests still show.
             */
            let linkedRows = [];
            try {
                const linkedRes = await getOrgModulesService({
                    org_code: authUser?.org_code, module_type: 'learning_program', page: 1, limit: 100,
                });
                const requestCodes = [
                    ...(Array.isArray(states) ? states.map((st) => st.program_code) : []),
                    ...docs.map((r) => r.program_code),
                ];
                linkedRows = linkedOnlyProgramRows(linkedRes?.data?.modules || [], requestCodes);
            } catch (e) {
                linkedRows = [];
            }
            if (filtering) {
                const matching = [...requestRows, ...linkedRows].filter((row) => row.status === availableStatusFilter);
                const start = availablePage * availableRowsPerPage;
                setAvailableRows(matching.slice(start, start + availableRowsPerPage));
                setAvailableTotal(matching.length);
            } else {
                const merged = mergeAvailablePage({
                    requestRows,
                    requestTotal: Number(res?.data?.total) || 0,
                    linkedRows,
                    page: availablePage,
                    rowsPerPage: availableRowsPerPage,
                });
                setAvailableRows(merged.rows);
                setAvailableTotal(merged.total);
            }
            /*
             * The counters describe ALL the school's programs, not this page:
             * one state per row the list can show, mapped by the same
             * toSlideStatus as the rows. Absent (older API, failed lookup) →
             * null, and the counters fall back to the rows on screen.
             */
            // …and the linked programs with no request are counted with them.
            setAvailableStatuses(Array.isArray(states)
                ? [...states.map(toSlideStatus), ...linkedRows.map((row) => row.status)]
                : null);
        } catch (e) {
            // An empty table and a failed fetch look identical otherwise.
            toast(TOAST_TYPES.ERROR, 'Could not load programs', e?.message || 'Please try again.');
            setAvailableRows([]);
            setAvailableStatuses(null);
        } finally {
            setAvailableLoading(false);
        }
    }, [availablePage, availableRowsPerPage, availableStatusFilter, authUser?.org_code]);

    useEffect(() => { if (isP1AAccount) loadAvailable(); }, [isP1AAccount, loadAvailable]);

    // Loaded once — the picker's options do not change with a filter or a page.
    useEffect(() => {
        if (!isP1AAccount || !authUser?.org_code) return undefined;
        let cancelled = false;
        /*
         * Both fall back to an empty list so a failure degrades to an empty
         * picker rather than a broken dialog — but it is logged, because an
         * empty org and a failed call otherwise look identical here, which
         * makes "no grades appear" impossible to diagnose from the UI.
         */
        const orLogEmpty = (what) => (e) => {
            console.warn(`[request wizard] could not load ${what}:`, e?.message || e);
            return [];
        };
        Promise.all([
            listOrgSchoolsService(authUser.org_code).catch(orLogEmpty('schools')),
            listOrgClassesService(authUser.org_code).catch(orLogEmpty('classes')),
        ]).then(([schools, classes]) => {
            if (cancelled) return;
            const schoolRows = schools || [];
            const classRows = classes || [];
            // The two numbers that explain an empty picker: how many CLASS rows
            // came back, and how many of them carry a grade (section_level).
            console.info('[request wizard] schools:', schoolRows.length,
                '| classes:', classRows.length,
                '| classes with a grade:', classRows.filter((r) => String(r.section_level || '').trim()).length);

            // institute_code -> school name, so a class can name its school.
            const schoolByCode = new Map(
                schoolRows
                    .filter((r) => r.institute_code)
                    .map((r) => [r.institute_code, r.institute_name || r.org_name || r.institute_code]),
            );

            setWizardSchools(schoolRows
                .filter((r) => r.institute_name || r.org_name)
                .map((r) => ({
                    value: r.institute_name || r.org_name,
                    label: r.institute_name || r.org_name,
                    institute_code: r.institute_code || '',
                })));

            /*
             * A network shares class names across schools — two schools both
             * having a "7A" is normal — so the label carries the school and the
             * institute_code travels with the request. A single school needs
             * neither: there is only one school it can mean.
             */
            setWizardClasses(classRows
                .filter((r) => r.section_name)
                .map((r) => {
                    const school = schoolByCode.get(r.institute_code);
                    return {
                        value: r.section_name,
                        label: school ? `${r.section_name} · ${school}` : r.section_name,
                        institute_code: r.institute_code || '',
                    };
                }));

            /*
             * Grades come from section_level on the class record — the field
             * Manage Classes shows in its "Grade" column. Distinct values only,
             * since many classes share a grade.
             *
             * NOT parsed from section_name: that returned the class itself on
             * names like "7A", and the single word "Grade" for every class
             * named "Grade 6 A".
             */
            /*
             * Options, like schools and classes, so the picker handles one shape.
             * A grade spans schools, so it carries no institute_code.
             */
            setWizardGrades([...new Set(
                classRows.map((r) => String(r.section_level || '').trim()).filter(Boolean),
            )].sort().map((g) => ({ value: g, label: g, institute_code: '' })));
        });
        return () => { cancelled = true; };
    }, [isP1AAccount, authUser?.org_code]);

    /*
     * The school answering a PARTNER-PROPOSED program (slides 47-49) has no
     * endpoint: nothing writes source = SUGGESTED yet, so no such row can
     * exist and the API was removed rather than left unreachable. Review on a
     * school-raised row opens the read-only preview below.
     */

    /*
     * The group a request is for, as the GROUP column and the pop-up's
     * "Class / group" box show it. In a network a class name alone does not say
     * which school it is, so a class reads "School Name - Class Name"
     * (LA-SNW-13Oct-02) and a whole-school request reads the school's name
     * (LA-SNW-13Oct-03).
     *
     * The school is the one saved on the request. A request saved before that
     * existed has none: its class still names the school when exactly one of
     * the network's schools has a class of that name, and anything else — a
     * grade, a school workspace, a school that cannot be found — keeps the
     * row's own text.
     */
    const groupLabelFor = (row) => {
        const raw = row?.raw || {};
        const fallback = row?.group || '';
        if (!authUser?.is_school_network) return fallback;
        const schoolOf = (code) => (wizardSchools.find((x) => x.institute_code === code) || {}).label || '';
        let school = raw.institute_code ? schoolOf(raw.institute_code) : '';
        if (!school && raw.class_name) {
            const codes = [...new Set(wizardClasses
                .filter((c) => c.value === raw.class_name && c.institute_code)
                .map((c) => c.institute_code))];
            if (codes.length === 1) school = schoolOf(codes[0]);
        }
        if (!school) return fallback;
        if (raw.class_name) return `${school} - ${raw.class_name}`;
        return raw.grade_level ? fallback : school;
    };
    const reviewGroupLabel = groupLabelFor(suggestionRow);
    // A whole-school request whose box now holds the school's name: the label
    // says "School", not "Class / group".
    const reviewGroupIsSchool = !!authUser?.is_school_network
        && !suggestionRow?.raw?.class_name && !suggestionRow?.raw?.grade_level
        && !!reviewGroupLabel && reviewGroupLabel !== (suggestionRow?.group || '');

    /** Slides 13-15 — one request per objective chosen. */
    const sendProgramRequests = async (objectives, details) => {
        try {
            /*
             * The target travels as what it IS.
             *
             * Every selection used to go out as grade_level whatever its type,
             * so a class or a school arrived at the partner labelled as a grade,
             * and class_name was never sent at all. Whole school sends neither —
             * the absence of both is what "whole school" means downstream, and
             * the partner's _groupLabel already renders that as "Whole School".
             */
            await Promise.all(objectives.flatMap((topic, i) => {
                const d = details[i] || {};
                const isClass = d.groupType === 'Class(es)';
                const isGrade = d.groupType === 'Grade(s)';
                const isSchool = d.groupType === 'School(s)';
                /*
                 * A network's School(s) pick is a list, held in groupInstituteCodes;
                 * groupInstituteCode is empty for it, so the request went out naming
                 * no school and read "Whole school" (LA-SNW-13Oct-03). One request
                 * per school picked, each carrying its own school.
                 */
                const schoolCodes = isSchool && Array.isArray(d.groupInstituteCodes) && d.groupInstituteCodes.length
                    ? d.groupInstituteCodes
                    : [(isClass || isSchool) ? (d.groupInstituteCode || '') : ''];
                return schoolCodes.map((institute_code) => createProgramRequestService({
                    topic,
                    num_sessions:          Number(d.sessions) || undefined,
                    session_duration_mins: Number(d.minutes) || undefined,
                    additional_info:       String(d.additional_info || '').trim() || undefined,
                    grade_level:           isGrade ? (d.groupValue || '') : '',
                    class_name:            isClass ? (d.groupValue || '') : '',
                    /*
                     * Which school inside the network this is for. A class
                     * name alone is ambiguous across a network, so the code
                     * that identifies the school travels with it.
                     */
                    institute_code,
                }));
            }));
            toast(TOAST_TYPES.SUCCESS, 'Requests sent', `${objectives.length} program request${objectives.length === 1 ? '' : 's'} sent to your partner.`);
            loadAvailable();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not send requests', e?.message || 'Please try again.');
        }
    };

    /*
     * P1A vs P1B, and why canCreateProduct rather than isProductEnabled.
     *
     * Both are plan enterprise_p1. What separates them is whether LearnCreator
     * has been assigned. But a level-22 school carries NO learning_program flag
     * of its own — the owner holds it — so the sidebar and the route guard both
     * add an `|| isP1School` fallback (PartialMainList.comp.jsx, and the program
     * branch of PrivateRouteWithLayout). That fallback makes isProductEnabled and
     * hasLearningPrograms true for EVERY P1 school, P1A included, so neither can
     * tell the two apart.
     *
     * canCreateProduct reads the `create` permission and carries no such
     * fallback — and "who may create programs" IS the P1A/P1B distinction.
     */
    if (isP1AAccount) {
        return (
            <>
                <AvailableLearningPrograms
                    // GROUP names the school in a network, as the pop-up does.
                    rows={availableRows.map((r) => ({ ...r, groupText: groupLabelFor(r) }))}
                    allStatuses={availableStatuses}
                    loading={availableLoading}
                    page={availablePage}
                    rowsPerPage={availableRowsPerPage}
                    total={availableTotal}
                    onPageChange={setAvailablePage}
                    onRowsPerPageChange={(n) => { setAvailableRowsPerPage(n); setAvailablePage(0); }}
                    statusFilter={availableStatusFilter}
                    onStatusFilterChange={(s) => {
                        // Clear the old rows at once so the table shows Loading, not stale results.
                        setAvailableRows([]);
                        setAvailableLoading(true);
                        setAvailableStatusFilter(s);
                        setAvailablePage(0);
                    }}
                    onRequestPrograms={() => setRequestWizardOpen(true)}
                    onAction={(action, row) => {
                        setSuggestionRow(row);
                        /*
                          * Review means two different things, by who raised the row.
                          *
                          * A PARTNER-suggested row (slide 46 / SW-47) opens the accept,
                          * adjust or decline dialog — this is the school answering.
                          * A row the school raised itself is the partner's to answer,
                          * so it stays a read-only preview.
                          */
                        if (action === 'Review') {
                            // Only while it is still waiting on the school. Once answered (changes
                            // asked, accepted), the API refuses a second answer, so it previews instead.
                            // Anything else — the school's own request, or an answered
                            // suggestion — opens the same popup read-only (SW-46 / SNW-64:
                            // "matching the suggested by partner popup layout; disable reselection").
                            setReviewReadOnly(!(String(row?.raw?.source) === 'suggested' && Number(row?.raw?.status) === WORKSPACE_REQUEST_STATUS.PENDING));
                            setSuggestionReviewOpen(true);
                        }

                        /*
                          * View opens the PROGRAM, not a name-only dialog.
                          *
                          * A delivered row carries program_code, which is what the
                          * program dashboard is keyed by. Without one there is nothing
                          * to open, so the preview stands in.
                          */
                        if (action === 'View') {
                            const code = row?.raw?.program_code;
                            if (code) history.push(getProgramPreviewPath(code));
                            else setPreviewOpen(true);
                        }

                        /*
                          * Link Programs → Manage Classes, which is where the
                          * existing per-class linking lives
                          * (linkSectionLearningProgramService). Linking is a
                          * class-by-class choice, so it belongs on the screen that
                          * lists the classes rather than being duplicated here.
                          */
                        /*
                          * PG-08/LINK: "A network links to its schools; a school
                          * links to a class." Confirmed by Ian on 4 Sep: a network
                          * request is reviewed by the network and "assigned
                          * accordingly to the school" — that assignment is this
                          * button. Sent to My Classes a network could only reach its
                          * own classes, never the schools inside it, so the program
                          * had nowhere to go.
                          */
                        if (action === 'Link Programs') {
                            history.push(authUser?.is_school_network ? '/dq_pro/clients/list' : '/dq_pro/classes');
                        }
                        /*
                         * Linked → View Dashboard (PID) per the slide 46/64 legend: the
                         * PROGRAM's impact page, not the dashboard overview. A network has
                         * no per-program page, so it opens its Learning Impact.
                         */
                        if (action === 'View Dashboard') {
                            const code = row?.raw?.program_code;
                            history.push(authUser?.is_school_network
                                ? '/dq_pro/network-dashboard/learning-programs/learning-impact'
                                : code
                                ? `/dq_pro/school-dashboard/learning-programs/learning-impact/program-impact?program_code=${encodeURIComponent(code)}`
                                : '/dq_pro/school-dashboard/overview');
                        }
                        // Program Ready: ask the partner for changes before they publish.
                        if (action === 'Request Changes') setChangesOpen(true);
                        // With an address the button is the mailto link itself (actionHref).
                        if (action === 'Contact Partner' && !partnerEmail) {
                            toast(TOAST_TYPES.ERROR, 'No contact address', 'Your partner has no contact email on record.');
                        }
                    }}
                    actionHref={(action, row) => (action === 'Contact Partner'
                        ? contactMailtoHref(partnerEmail, `${row.program} request`)
                        : '')}
                />
                <ProgramPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)}
                                      programName={suggestionRow?.program || ''} />

                {/*
                  * Program Ready → Request Changes (Ian / Usman, 2 Oct 2026): the
                  * message is saved on the request and shown on the partner's
                  * Pipeline row. The build and its status are untouched.
                  */}
                <RequestProgramChangesDialog
                    open={changesOpen}
                    onClose={() => setChangesOpen(false)}
                    programName={suggestionRow?.program || ''}
                    sending={respondingSuggestion}
                    // Same cohort fields as the partner's Edit cohort, opened on what the row targets.
                    initial={suggestionRow?.raw || null}
                    sessions={suggestionRow?.raw?.num_sessions}
                    grades={wizardGrades}
                    classes={wizardClasses}
                    isNetwork={!!authUser?.is_school_network}
                    onSend={async (text) => {
                        const request_id = suggestionRow?.raw?._id;
                        if (!request_id) return;
                        setRespondingSuggestion(true);
                        try {
                            const res = await respondToSuggestionService({
                                request_id,
                                status: WORKSPACE_REQUEST_STATUS.CHANGES_REQUESTED,
                                requested_changes: text,
                            });
                            if (res?.error) {
                                toast(TOAST_TYPES.ERROR, 'Could not send your request', res.error_message || 'Please try again.');
                                return;
                            }
                            // Lock the row at once — the reload below takes a moment, and
                            // until it lands the button would still be clickable.
                            setAvailableRows((prev) => prev.map((r) => (r.id === request_id ? { ...r, pendingChange: true } : r)));
                            setChangesOpen(false);
                            toast(TOAST_TYPES.SUCCESS, 'Request sent', 'Your partner will update the program before publishing it.');
                            loadAvailable();
                        } catch (e) {
                            toast(TOAST_TYPES.ERROR, 'Could not send your request', e?.message || 'Please try again.');
                        } finally {
                            setRespondingSuggestion(false);
                        }
                    }}
                />

                <ProgramSuggestionReviewDialog
                    open={suggestionReviewOpen}
                    onClose={() => setSuggestionReviewOpen(false)}
                    area={suggestionRow?.raw?.topic || suggestionRow?.program || ''}
                    group={groupKeyFor(suggestionRow?.raw?.grade_level, suggestionRow?.raw?.class_name)}
                    groupOptions={suggestionGroupOptions}
                    /*
                     * Only what the suggestion carries. Left empty, the dialog
                     * starts from slide 47's 5 sessions x 45 minutes — "1 sessions"
                     * was the page's own guess and matched no option.
                     */
                    sessions={suggestionRow?.raw?.num_sessions
                        ? `${suggestionRow.raw.num_sessions} session${Number(suggestionRow.raw.num_sessions) === 1 ? '' : 's'}`
                        : undefined}
                    sending={respondingSuggestion}
                    readOnly={reviewReadOnly}
                    requestedBySchool={reviewReadOnly && String(suggestionRow?.raw?.source) !== 'suggested'}
                    groupLabel={reviewGroupLabel}
                    groupFieldLabel={reviewGroupIsSchool ? 'School' : undefined}
                    // Cohort with the same fields as the partner's Edit cohort. A suggestion is
                    // for one school: in a network, only that school's classes.
                    initial={suggestionRow?.raw || null}
                    grades={wizardGrades}
                    classes={suggestionRow?.raw?.institute_code
                        ? wizardClasses.filter((c) => !c.institute_code || c.institute_code === suggestionRow.raw.institute_code)
                        : wizardClasses}
                    onAccept={(p) => respondToSuggestion(WORKSPACE_REQUEST_STATUS.ACCEPTED, p)}
                    onRequestChanges={(p) => respondToSuggestion(WORKSPACE_REQUEST_STATUS.CHANGES_REQUESTED, p)}
                    onDecline={(p) => respondToSuggestion(WORKSPACE_REQUEST_STATUS.DECLINED, p)}
                />
                <ProgramRequestSentDialog open={suggestionSentOpen} onClose={() => setSuggestionSentOpen(false)} />
                {/* SW/SNW slides 13-15 — Request learning programs. */}
                <RequestLearningProgramsWizard
                    open={requestWizardOpen}
                    onClose={() => setRequestWizardOpen(false)}
                    partnerName={authUser?.partner_name || 'your DQ Partner'}
                    schools={wizardSchools}
                    classes={wizardClasses}
                    grades={wizardGrades}
                    // A network picks among its schools; a school does not.
                    isNetwork={!!authUser?.is_school_network}
                    onSend={sendProgramRequests}
                />
            </>
        );
    }

    // Status counts for the filter chips — scoped to the currently loaded
    // page only (see the searchQuery/statusFilter state comment above).
    const draftLearnerCount = learnerPrograms.filter((p) => !isPublished(p.status)).length;
    const publishedLearnerCount = learnerPrograms.length - draftLearnerCount;
    const filteredLearnerPrograms = learnerPrograms.filter((p) => {
        if (statusFilter === 'published' && !isPublished(p.status)) return false;
        if (statusFilter === 'draft' && isPublished(p.status)) return false;
        const q = searchQuery.trim().toLowerCase();
        if (q && !String(p.full_name || '').toLowerCase().includes(q)) return false;
        return true;
    });

    return (
        <Box sx={{ paddingTop:'18px' }}>

            {/*
              * Confirm before deleting a draft. Same Dialog primitives as the
              * rest of this page; the draft name is repeated back so the person
              * can see WHICH one is about to go.
              */}
            <Dialog
                open={Boolean(draftPendingDelete)}
                onClose={() => setDraftPendingDelete(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', mb: 1 }}>
                        {draftPendingDelete?.isProgram ? 'Delete this program?' : 'Delete this draft?'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: 'var(--brand-text-muted, #6B7280)' }}>
                        <strong>{draftPendingDelete?.title || 'Untitled program'}</strong>
                        {draftPendingDelete?.isProgram
                            ? ' and everything generated for it will be removed. This cannot be undone.'
                            : ' and its conversation will be removed. This cannot be undone.'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                        <Button
                            onClick={() => setDraftPendingDelete(null)}
                            disabled={Boolean(deletingDraftId)}
                            sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #6B7280)' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={Boolean(deletingDraftId)}
                            onClick={() => {
                                const target = draftPendingDelete;
                                setDraftPendingDelete(null);
                                if (target?.isProgram) handleDeleteProgram(target);
                                else handleDeleteDraft(target?.program_id);
                            }}
                            sx={{ textTransform: 'none', borderRadius: '10px', backgroundColor: '#DC2626 !important', '&:hover': { backgroundColor: '#B91C1C !important' } }}
                        >
                            {deletingDraftId ? 'Deleting…' : 'Delete'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={Boolean(renameTarget)}
                onClose={() => { if (!renaming) setRenameTarget(null); }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', mb: 2 }}>
                        Rename program
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Program name"
                        value={renameTarget?.name || ''}
                        onChange={(e) => { const { value } = e.target; setRenameTarget((t) => (t ? { ...t, name: value } : t)); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameProgram(); }}
                        disabled={renaming}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                        <Button
                            onClick={() => setRenameTarget(null)}
                            disabled={renaming}
                            sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #6B7280)' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={renaming || !String(renameTarget?.name || '').trim() || String(renameTarget?.name || '').trim() === renameTarget?.original}
                            onClick={handleRenameProgram}
                            sx={{ textTransform: 'none', borderRadius: '10px' }}
                        >
                            {renaming ? 'Saving…' : 'Save'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <ExistingProgramDialog
                open={resumePromptOpen}
                busy={startingNew}
                onClose={() => setResumePromptOpen(false)}
                onContinue={() => { setResumePromptOpen(false); goToBuilder(); }}
                onStartNew={handleStartNewProgram}
            />

            <Box className={classes.pageHeaderRow}>
                <Box>
                    <Typography className={classes.pageTitle}>My Programs</Typography>
                    <Typography className={classes.pageSubtitle}>
                        Programs you have created or adapted, and the assessments they draw on.
                    </Typography>
                </Box>
                <Button className={classes.newProgramBtn} onClick={handleNewProgramClick}>
                    + New program
                </Button>
            </Box>

            <Box className={classes.filterBar}>
                <TextField
                    className={classes.searchField}
                    size="small"
                    variant="outlined"
                    placeholder="Search programs"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon style={{ fontSize: 20, color: 'var(--brand-text-muted, #9CA3AF)' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    className={`${classes.filterChip} ${statusFilter === 'all' ? classes.filterChipActive : ''}`}
                    onClick={() => setStatusFilter('all')}
                >
                    All {learnerPrograms.length}
                </Button>
                <Button
                    className={`${classes.filterChip} ${statusFilter === 'published' ? classes.filterChipActive : ''}`}
                    onClick={() => setStatusFilter('published')}
                >
                    Published {publishedLearnerCount}
                </Button>
                <Button
                    className={`${classes.filterChip} ${statusFilter === 'draft' ? classes.filterChipActive : ''}`}
                    onClick={() => setStatusFilter('draft')}
                >
                    Draft {draftLearnerCount}
                </Button>
            </Box>

            {/* ── In-progress conversations ────────────────────────────────── */}
            {drafts.length > 0 && (
                <Box style={{ marginBottom: 20 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <HourglassEmptyIcon style={{ color: '#FF6900', fontSize: 20 }} />
                        <Typography className={classes.sectionTitle} style={{ marginBottom: '0 !important', marginTop: '0 !important' }}>
                            Continue where you left off
                        </Typography>
                    </Box>
                    <Typography className={classes.pageSubtitle} style={{ marginBottom: 16 }}>
                        Conversations you started but have not built yet.
                    </Typography>

                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {drafts.map((d) => (
                            <Box
                                key={d.program_id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '0 16px',
                                    // Fixed, not content-driven. Row height was falling out
                                    // of the text metrics, so a one-line title and a wrapped
                                    // one produced different-sized cards down the list.
                                    minHeight: 68,
                                    border: '1px solid var(--brand-border, #EDE7E1)',
                                    borderRadius: 12,
                                    background: 'var(--brand-surface, #fff)',
                                }}
                            >
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Typography style={{
                                        fontSize: 15, fontWeight: 600, lineHeight: '22px',
                                        color: 'var(--brand-text, #1a1a1a)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {/* Unnamed until the assistant knows the topic and audience. */}
                                        {titleCase(d.title) || 'Untitled program'}
                                    </Typography>
                                    <Typography style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--brand-text-muted, #6B7280)' }}>
                                        {d.message_count > 0 ? `${d.message_count} messages` : 'Not started'}
                                        {d.last_activity ? ` · ${new Date(d.last_activity).toLocaleDateString('en-US')}` : ''}
                                    </Typography>
                                </Box>

                                {d.build_failed ? (
                                    <Tooltip arrow title={REGENERATE_TOOLTIP}>
                                        <span>
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                disabled={regeneratingId === d.program_id}
                                                onClick={() => handleRegenerateDraft(d)}
                                                className={classes.useTemplateBtn}
                                            >
                                                {regeneratingId === d.program_id ? 'Regenerating...' : 'Regenerate'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    // resumeDraftId, not programCode: this is a
                                    // conversation, not a built program, and the
                                    // programCode route state would send the builder
                                    // looking for a module that does not exist yet.
                                    onClick={() => history.push('/dq_pro/program/creation/preview', { resumeDraftId: d.program_id })}
                                    // The same button as Use Template below: one pill
                                    // shape, one hover, defined once.
                                    className={classes.useTemplateBtn}
                                >
                                    Continue
                                </Button>
                                )}

                                <Tooltip arrow title="Delete this draft">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={deletingDraftId === d.program_id}
                                            onClick={() => setDraftPendingDelete(d)}
                                            sx={{ color: 'var(--brand-text-muted, #9CA3AF)', '&:hover': { color: '#DC2626' } }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* ── Sample Programs (DQ-authored showcase templates, global, preview-only) ── */}
            {templatePrograms.length > 0 && (
                <Box style={{ marginBottom: 20 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <StarIcon style={{ color: '#FF6900', fontSize: 22 }} />
                        <Typography className={classes.sectionTitle} style={{ marginBottom: '0 !important', marginTop: '0 !important' }}>
                            Start from a template
                        </Typography>
                    </Box>
                    <Typography className={classes.pageSubtitle} style={{ marginBottom: 16 }}>
                        DQ-authored programs you can copy and adapt.
                    </Typography>

                    <Box>
                        {templatePrograms.map((p) => {
                            const tKey = `tmpl_${p.code}`;
                            const isExpanded = expandedDescriptions[tKey];
                            const hasMore = p.description && p.description.split(' ').length > 15;
                            return (
                                <Box key={p.code || tKey} className={classes.libraryCard}>
                                    <Box className={classes.libraryCardTopRow}>
                                        <Box style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <Typography className={classes.libraryTitle}>{p.full_name}</Typography>
                                            {p.template_category && (
                                                <Typography style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                    {p.template_category}
                                                </Typography>
                                            )}
                                            {p.description && (
                                                <Typography className={classes.libraryDescription}>
                                                    {isExpanded ? p.description : truncate(p.description)}
                                                    {hasMore && (
                                                        <span className={classes.readMoreLink} onClick={() => toggleDescription(tKey)}>
                                                            {isExpanded ? 'Read less' : 'Read more'}
                                                        </span>
                                                    )}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap', flexShrink: 0 }}>
                                            {p.time_to_complete_minutes && (
                                                <Box className={classes.libraryMetaRow} style={{ flexShrink: 0 }}>
                                                    <Box className={classes.metaCol}>
                                                        <Typography className={classes.metaLabel}>Time</Typography>
                                                        <Typography className={classes.metaValue}>{p.time_to_complete_minutes} min</Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box className={classes.libraryActionsRow}>
                                                <Button
                                                    className={classes.actionBtn}
                                                    variant="outlined"
                                                    onClick={() => handlePreviewTemplate(p)}
                                                >
                                                    Preview
                                                </Button>
                                                <Button className={classes.useTemplateBtn} onClick={handleUseTemplate}>
                                                    Use Template
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* ── In-Progress Insider Programs (level 21 paid only, hidden when empty) ── */}
            {isLevel21 && isPaidUser && insiderPrograms.length > 0 && (
                <Box style={{ marginBottom: 48 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <HourglassEmptyIcon style={{ color: '#FF6900', fontSize: 22 }} />
                        <Typography className={classes.sectionTitle} style={{ marginBottom: '0 !important', marginTop: '0 !important' }}>
                            In-Progress Programs
                        </Typography>
                    </Box>

                    <Box style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '10px' }}>
                        <Box className={classes.inProgressHeader}>
                            <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Program Name</Typography>
                            <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Description</Typography>
                            <Typography className={classes.headerCell}>Time (min)</Typography>
                            <Typography className={classes.headerCell}>Status</Typography>
                            <Typography className={classes.headerCell}></Typography>
                        </Box>

                        {insiderPrograms.map((p) => {
                            const ipKey = `ip_${p.code}`;
                            const isExpanded = expandedDescriptions[ipKey];
                            const hasMore = p.description && p.description.split(' ').length > 15;
                            return (
                                <Box key={p.code || ipKey} className={classes.inProgressRow}>
                                    {/* Desktop cells */}
                                    <Box className={classes.desktopOnly} style={{ minWidth: 0 }}>
                                        <Typography className={classes.cellTextLeft}>{p.full_name}</Typography>
                                        <Tooltip title={p.code}>
                                            <Typography className={classes.truncate} style={{ fontSize: 12, color: 'var(--brand-text-muted, #9CA3AF)', textAlign: 'left' }}>({p.code})</Typography>
                                        </Tooltip>
                                    </Box>
                                    <Typography className={`${classes.cellTextLeft} ${classes.desktopOnly}`}>
                                        {isExpanded ? p.description : truncate(p.description)}
                                        {hasMore && (
                                            <span
                                                className={classes.readMoreLink}
                                                onClick={() => toggleDescription(ipKey)}
                                            >
                                                {isExpanded ? 'Read less' : 'Read more'}
                                            </span>
                                        )}
                                    </Typography>
                                    <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>{p.time_to_complete_minutes || '—'}</Typography>
                                    <Box className={classes.desktopOnly} style={{ display: 'flex', justifyContent: 'center' }}>
                                        <ProgramPublishBadge status={p.status} />
                                    </Box>
                                    <Box className={classes.desktopOnly} style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                                        {renderProgramRecoveryButtons(p)}
                                        {isPublished(p.status) ? null : (
                                            <Tooltip title={publishButtonTooltip(p)} arrow>
                                                <span>
                                                    <Button
                                                        className={classes.actionBtn}
                                                        variant="outlined"
                                                        disabled={publishingCode === p.code || !p.is_ready}
                                                        onClick={() => openPublishConfirm(p)}
                                                    >
                                                        {publishingCode === p.code ? 'Publishing...' : 'Publish'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Button className={classes.viewBtn} onClick={() => handleViewProgram(p)}>
                                            View Program
                                        </Button>
                                    </Box>
                                    {/* Mobile card */}
                                    <Box className={classes.mobilePrimary}>
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <Typography className={classes.mobilePrimaryText}>{p.full_name}</Typography>
                                                <ProgramPublishBadge status={p.status} />
                                            </Box>
                                            <Typography className={classes.mobileSubText}>{p.code}</Typography>
                                        </Box>
                                        <Box className={classes.mobileActions}>
                                            {renderProgramRecoveryButtons(p)}
                                            {isPublished(p.status) ? null : (
                                                <Tooltip title={publishButtonTooltip(p)} arrow>
                                                    <span>
                                                        <Button
                                                            className={classes.actionBtn}
                                                            variant="outlined"
                                                            disabled={publishingCode === p.code || !p.is_ready}
                                                            onClick={() => openPublishConfirm(p)}
                                                        >
                                                            {publishingCode === p.code ? 'Publishing...' : 'Publish'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Button className={classes.viewBtn} onClick={() => handleViewProgram(p)}>
                                                View Program
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Box className={classes.mobileMetaRow}>
                                        {p.time_to_complete_minutes && (
                                            <span className={classes.mobileMetaTag}>{p.time_to_complete_minutes} min</span>
                                        )}
                                        {p.description && (
                                            <span className={classes.mobileMetaTag}>{truncate(p.description, 4)}</span>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* ── Org / Main Programs Table ──
                 Same guard as the list Box just below — this title used to be
                 gated on isLevel21 && isPaidUser only, so it disappeared for
                 every other role (level 22, non-paid level 21, level 20...)
                 while the list itself still rendered underneath it. */}
            {(!isLevel21 || !isPaidUser || insiderPrograms.length === 0 || learnerPrograms.length > 0) && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <LibraryBooksIcon style={{ color: '#FF6900', fontSize: 22 }} />
                    <Typography className={classes.sectionTitle} style={{ marginBottom: '0 !important', marginTop: '0 !important' }}>
                        My program library
                    </Typography>
                    <Typography className={classes.pageSubtitle}>{learnerPrograms.length} programs</Typography>
                </Box>
            )}

            {(!isLevel21 || !isPaidUser || insiderPrograms.length === 0 || learnerPrograms.length > 0) && <Box className={classes.tableContent} style={{ marginTop: 16 }}>
                {filteredLearnerPrograms.length > 0 ? filteredLearnerPrograms.map((p) => {
                    const isExpanded = expandedDescriptions[p.code];
                    const hasMore = p.description && p.description.split(' ').length > 15;
                    const published = isPublished(p.status);
                    const canManage = canManageProgram(p);
                    return (
                        <Box key={p.code} className={classes.libraryCard}>
                            <Box className={classes.libraryCardTopRow}>
                                <Box style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Box className={classes.libraryTitleGroup}>
                                    <Typography className={classes.libraryTitle}>{p.full_name}</Typography>
                                    <ProgramPublishBadge status={p.status} />
                                    {/* creator_role is null on programs that predate attribution — those
                                        render as partner-built, not as a teacher's (spec §8). */}
                                    {p.creator_role === 'teacher' && (
                                        <Chip label="Built by your teacher" size="small" style={{ color: '#fff', background: '#FF6900', fontSize: 11 }} />
                                    )}
                                </Box>
                                {p.description && (
                                    <Typography className={classes.libraryDescription}>
                                        {isExpanded ? p.description : truncate(p.description)}
                                        {hasMore && (
                                            <span className={classes.readMoreLink} onClick={() => toggleDescription(p.code)}>
                                                {isExpanded ? 'Read less' : 'Read more'}
                                            </span>
                                        )}
                                    </Typography>
                                )}
                                <Typography className={classes.libraryCode}>{p.code}</Typography>
                                </Box>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap', flexShrink: 0 }}>
                                    <Box className={classes.libraryMetaRow} style={{ flexShrink: 0 }}>
                                        {p.time_to_complete_minutes && (
                                            <Box className={classes.metaCol}>
                                                <Typography className={classes.metaLabel}>Time</Typography>
                                                <Typography className={classes.metaValue}>{p.time_to_complete_minutes} min</Typography>
                                            </Box>
                                        )}
                                        {p.createdAt && (
                                            <Box className={classes.metaCol}>
                                                {/* Only a creation timestamp exists — no separate "last edited"
                                                    field — so a draft is honestly labeled "Created", not
                                                    "Last edited" (the mockup's wording implies edit tracking
                                                    this app doesn't have). */}
                                                <Typography className={classes.metaLabel}>{published ? 'Published' : 'Created'}</Typography>
                                                <Typography className={classes.metaValue}>{formatDateTime(p.createdAt)}</Typography>
                                            </Box>
                                        )}
                                        {isOrgScopedUser && (
                                            <Box className={classes.metaCol}>
                                                <Typography className={classes.metaLabel}>Visibility</Typography>
                                                <Typography className={classes.metaValue}>{formatVisibilityScope(p.visibility_scope)}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box className={classes.libraryActionsRow}>
                                        {/* Published: assign it one level down — "Assign to schools"
                                            for partners and networks, "Assign to classes" for a school
                                            (a single school has no schools to assign to — Usman,
                                            7 Oct 2026). Teachers get no button. */}
                                        {(!published || assignTarget) && (
                                            failedPrograms[p.code] && !published && canManage ? (
                                                <Tooltip title={REGENERATE_TOOLTIP} arrow>
                                                    <span>
                                                        <Button
                                                            className={classes.viewBtn}
                                                            disabled={regeneratingId === p.code}
                                                            onClick={() => handleRegenerateProgram(p)}
                                                        >
                                                            {regeneratingId === p.code ? 'Regenerating...' : 'Regenerate'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                            <Button
                                                className={classes.viewBtn}
                                                onClick={() => (published ? handleAssignToSchools(p) : handleViewProgram(p))}
                                            >
                                                {published
                                                    ? (assignTarget === 'classes' ? 'Assign to classes' : 'Link to Schools')
                                                    : 'Continue editing'}
                                            </Button>
                                            )
                                        )}
                                        <ProgramRowKebabMenu
                                            items={[
                                                !isLevel22 && !published && canManage ? {
                                                    label: publishingCode === p.code ? 'Publishing...' : 'Publish',
                                                    onClick: () => openPublishConfirm(p),
                                                    disabled: publishingCode === p.code || !p.is_ready,
                                                    tooltip: publishButtonTooltip(p),
                                                } : null,
                                                {
                                                    label: published ? 'View Program' : 'Continue editing',
                                                    onClick: () => handleViewProgram(p),
                                                },
                                                {
                                                    label: 'Preview as learner',
                                                    // The existing view-only preview: the program as learners
                                                    // see it, without the chat, editing or publishing (LA-PW-19Oct-03).
                                                    onClick: () => history.push('/dq_pro/program/creation/preview', { programCode: p.code, viewOnly: true }),
                                                    disabled: !p.is_ready,
                                                    tooltip: p.is_ready
                                                        ? 'See this program the way a learner would experience it.'
                                                        : 'Available once the program has finished building.',
                                                },
                                                canCreatePrograms ? {
                                                    label: duplicatingCode === p.code ? 'Duplicating...' : 'Duplicate',
                                                    onClick: () => openDuplicateConfirm(p),
                                                    disabled: duplicatingCode === p.code || !p.is_ready,
                                                    tooltip: duplicateButtonTooltip(p),
                                                } : null,
                                                {
                                                    label: 'View Assessment',
                                                    // The program's pre and post assessment: the view-only preview
                                                    // on its Assess tab (LA-PW,SNW,SW,TW-19Oct-01).
                                                    onClick: () => history.push('/dq_pro/program/creation/preview', { programCode: p.code, viewOnly: true, openTab: 2 }),
                                                    disabled: !p.is_ready,
                                                    tooltip: p.is_ready
                                                        ? 'View the assessments linked to this program.'
                                                        : 'Available once the program has finished building.',
                                                },
                                                canManage ? {
                                                    label: 'Rename',
                                                    onClick: () => setRenameTarget({ code: p.code, name: p.full_name || '', original: p.full_name || '' }),
                                                    tooltip: "Change this program's name.",
                                                } : null,
                                                canManage && !published ? {
                                                    label: deletingDraftId === p.code ? 'Deleting...' : 'Delete',
                                                    onClick: () => askDeleteProgram(p),
                                                    disabled: deletingDraftId === p.code,
                                                    tooltip: 'Permanently remove this draft.',
                                                    danger: true,
                                                } : null,
                                            ].filter(Boolean)}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Not-ready warning banner — temporarily hidden per request
                                (its text doesn't match a real, trackable reason yet).
                                Commented out rather than deleted for an easy revert. */}
                            {false && !published && !p.is_ready && canManage && (
                                <Box className={classes.warningBanner}>
                                    Publish is blocked: one module is empty and no assessment is linked.
                                </Box>
                            )}
                        </Box>
                    );
                }) :  (
                    <Box style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        borderRadius: '16px',
                        border: '1px dashed rgba(241, 90, 36, 0.3)',
                    }}>
                        <LibraryBooksIcon style={{ fontSize: 56, color: '#FF6900', opacity: 0.35, marginBottom: 16 }} />
                        <Typography style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #333)', marginBottom: 8 }}>
                            {learnerPrograms.length === 0 ? 'No learning programs yet' : 'No programs match your search'}
                        </Typography>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', textAlign: 'center', maxWidth: 400 }}>
                            {learnerPrograms.length === 0
                                ? "You haven't created any learning programs. Use the workspace to build and deploy your first program."
                                : 'Try a different search term or filter.'}
                        </Typography>
                    </Box>
                )}

                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>}

            <LoaderWithBackDrop loading={loading} />
            <AssignProgramToSchoolsDrawer
                open={!!assignProgram}
                program={assignProgram}
                authUser={authUser}
                target={assignTarget || 'schools'}
                onClose={() => setAssignProgram(null)}
                onAssigned={() => fetchPrograms(page + 1, rowsPerPage)}
            />
            <PublishProgramDialogs
                stage={publishDialog?.stage || null}
                publishing={!!publishDialog && publishingCode === publishDialog.program?.code}
                onGoBack={() => setPublishDialog(null)}
                onConfirmPublish={() => publishDialog?.program && handlePublish(publishDialog.program)}
                onGoToMaterials={handleGoToMaterials}
                onLinkToSchool={handleLinkToSchool}
                deliveredSchoolName={deliveredTarget?.school_name || ''}
            />

            <DuplicateProgramDialogs
                stage={duplicateDialog?.stage || null}
                duplicating={!!duplicateDialog && duplicatingCode === duplicateDialog.program?.code}
                name={duplicateDialog?.name || ''}
                onNameChange={(value) => setDuplicateDialog((prev) => (prev ? { ...prev, name: value } : prev))}
                onCancel={() => setDuplicateDialog(null)}
                onConfirmDuplicate={handleDuplicate}
                duplicatedName={duplicateDialog?.created?.full_name || ''}
                onPreview={handlePreviewDuplicate}
                onStayOnList={() => setDuplicateDialog(null)}
            />

            {/* One step before the link (Lydia, slide 40 thread): name the school
                so the owner can see they are linking to the right one, then link
                from here — no detour through the client list. */}
            <Dialog
                open={linkConfirmOpen}
                onClose={() => !linkingToSchool && setLinkConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--brand-text, #052F44)', mb: 1.5 }}>
                        Program published
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--brand-text, #555)', mb: 3 }}>
                        Link it to <strong>{deliveredTarget?.school_name || 'this school'}</strong> so they
                        can open it from their workspace.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setLinkConfirmOpen(false)}
                            disabled={linkingToSchool}
                            sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 700, px: 2, fontSize: 13, lineHeight: 1.2 }}
                        >
                            Later
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={confirmLinkToSchool}
                            disabled={linkingToSchool}
                            sx={{
                                background: '#FF6900', color: '#fff', borderRadius: '50px',
                                textTransform: 'none', fontWeight: 700, px: 2, fontSize: 13,
                                whiteSpace: 'nowrap', boxShadow: 'none',
                                '&:hover': { background: '#c9541e' },
                                '&.Mui-disabled': { background: '#FF6900', color: '#fff', opacity: 0.6 },
                            }}
                        >
                            {linkingToSchool ? 'Linking…' : 'Link Program to School'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

        </Box>
    );
};

export default DQProLearnerProgramList;
